import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "@/i18n/I18nContext";
import { AdminAuthProvider } from "@/auth/AdminAuthContext";
import { OrganizerAuthProvider } from "@/auth/OrganizerAuthContext";
import ProtectedAdminRoute from "@/auth/ProtectedAdminRoute";
import ProtectedOrganizerRoute from "@/auth/ProtectedOrganizerRoute";
import HomePage from "@/pages/HomePage";
import EventsListPage from "@/pages/EventsListPage";
import AiAdvisorPage from "@/pages/AiAdvisorPage";
import ResourcesPage from "@/pages/ResourcesPage";
import SpeakersDirectoryPage from "@/pages/SpeakersDirectoryPage";
import SponsorsDirectoryPage from "@/pages/SponsorsDirectoryPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import NailEventLanding from "@/pages/NailEventLanding";
import NailEventCheckIn from "@/pages/NailEventCheckIn";
import NailEventDoorKiosk from "@/pages/NailEventDoorKiosk";
import EventCheckinKiosk from "@/pages/EventCheckinKiosk";
import KioskLoginPage from "@/pages/KioskLoginPage";
import NailEventAdmin from "@/pages/NailEventAdmin";
import AdminLogin from "@/pages/AdminLogin";
import AdminEventsList from "@/pages/AdminEventsList";
import AdminEventEditor from "@/pages/AdminEventEditor";
import AdminSpeakerLibrary from "@/pages/AdminSpeakerLibrary";
import AdminBoardOfDirectors from "@/pages/AdminBoardOfDirectors";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminSponsorLibrary from "@/pages/AdminSponsorLibrary";
import AdminMediaLibrary from "@/pages/AdminMediaLibrary";
import AdminKnowledgeLibrary from "@/pages/AdminKnowledgeLibrary";
import AdminKnowledgeImport from "@/pages/AdminKnowledgeImport";
import BoardOfDirectorsPage from "@/pages/BoardOfDirectorsPage";
import AdminAiBrain from "@/pages/AdminAiBrain";
import AdminArticles from "@/pages/AdminArticles";
import AdminTrainingDashboard from "@/pages/AdminTrainingDashboard";
import OrganizerSignup from "@/pages/OrganizerSignup";
import OrganizerLogin from "@/pages/OrganizerLogin";
import OrganizerSetup from "@/pages/OrganizerSetup";
import OrganizerForgotPassword from "@/pages/OrganizerForgotPassword";
import OrganizerResetPassword from "@/pages/OrganizerResetPassword";
import OrganizerVerifyEmail from "@/pages/OrganizerVerifyEmail";
import OrganizerSecurity from "@/pages/OrganizerSecurity";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import OrganizerEventEditor from "@/pages/OrganizerEventEditor";
import OrganizerEventRegistrations from "@/pages/OrganizerEventRegistrations";
import WallBoard from "@/pages/WallBoard";
import WallOfFoundersPage from "@/pages/WallOfFoundersPage";
import { LearningCenterListPage, LearningArticlePage } from "@/pages/LearningCenter";

const Guard = ({ children }) => <ProtectedAdminRoute>{children}</ProtectedAdminRoute>;
const OrgGuard = ({ children }) => <ProtectedOrganizerRoute>{children}</ProtectedOrganizerRoute>;

function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AdminAuthProvider>
          <OrganizerAuthProvider>
          <Routes>
            {/* Community homepage */}
            <Route path="/" element={<HomePage />} />

            {/* Public modules */}
            <Route path="/events" element={<EventsListPage />} />
            <Route path="/events/:slug" element={<NailEventLanding />} />
            <Route path="/events/:slug/checkin" element={<NailEventDoorKiosk />} />
            <Route path="/events/:slug/checkin-staff" element={<NailEventCheckIn />} />
            <Route path="/events/:slug/checkin-kiosk" element={<EventCheckinKiosk />} />
            <Route path="/checkin" element={<KioskLoginPage />} />
            <Route path="/ai" element={<AiAdvisorPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/speakers" element={<SpeakersDirectoryPage />} />
            <Route path="/sponsors" element={<SponsorsDirectoryPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/organizers" element={<WallOfFoundersPage />} />
            <Route path="/board" element={<BoardOfDirectorsPage />} />

            {/* Back-compat: keep old URLs working */}
            <Route path="/nail-event/:slug" element={<NailEventLanding />} />
            <Route path="/nail-event/:slug/checkin" element={<NailEventDoorKiosk />} />
            <Route path="/nail-event/:slug/checkin-staff" element={<NailEventCheckIn />} />

            {/* Admin login (public) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />

            {/* Admin (protected) */}
            <Route path="/admin" element={<Navigate to="/admin/nail-events" replace />} />
            <Route path="/admin/nail-events" element={<Guard><AdminEventsList /></Guard>} />
            <Route path="/admin/nail-events/:slug/edit" element={<Guard><AdminEventEditor /></Guard>} />
            <Route path="/admin/nail-events/:slug" element={<Guard><NailEventAdmin /></Guard>} />
            <Route path="/admin/library/speakers" element={<Guard><AdminSpeakerLibrary /></Guard>} />
            <Route path="/admin/board" element={<Guard><AdminBoardOfDirectors /></Guard>} />
            <Route path="/admin/library/sponsors" element={<Guard><AdminSponsorLibrary /></Guard>} />
            <Route path="/admin/library/media" element={<Guard><AdminMediaLibrary /></Guard>} />
            <Route path="/admin/library/knowledge" element={<Guard><AdminKnowledgeLibrary /></Guard>} />
            <Route path="/admin/library/knowledge/import" element={<Guard><AdminKnowledgeImport /></Guard>} />
            <Route path="/admin/library/ai" element={<Guard><AdminAiBrain /></Guard>} />
            <Route path="/admin/library/articles" element={<Guard><AdminArticles /></Guard>} />
            <Route path="/admin/training" element={<Guard><AdminTrainingDashboard /></Guard>} />

            {/* Public Learning Center */}
            <Route path="/learn" element={<LearningCenterListPage />} />
            <Route path="/learn/:slug" element={<LearningArticlePage />} />

            {/* Organizer self-serve portal (Phase 6) */}
            <Route path="/organizer" element={<Navigate to="/organizer/dashboard" replace />} />
            <Route path="/organizer/signup" element={<OrganizerSignup />} />
            <Route path="/organizer/login" element={<OrganizerLogin />} />
            <Route path="/organizer/setup" element={<OrganizerSetup />} />
            <Route path="/organizer/forgot-password" element={<OrganizerForgotPassword />} />
            <Route path="/organizer/reset-password" element={<OrganizerResetPassword />} />
            <Route path="/organizer/verify-email" element={<OrganizerVerifyEmail />} />
            <Route path="/organizer/security" element={<OrgGuard><OrganizerSecurity /></OrgGuard>} />
            <Route path="/organizer/dashboard" element={<OrgGuard><OrganizerDashboard /></OrgGuard>} />
            <Route path="/organizer/events/new" element={<OrgGuard><OrganizerEventEditor /></OrgGuard>} />
            <Route path="/organizer/events/:slug/edit" element={<OrgGuard><OrganizerEventEditor /></OrgGuard>} />
            <Route path="/organizer/events/:slug/registrations" element={<OrgGuard><OrganizerEventRegistrations /></OrgGuard>} />
            <Route path="/organizer/events/:slug/wall" element={<OrgGuard><WallBoard /></OrgGuard>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </OrganizerAuthProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}

export default App;
